import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { fmtDate, PAYMENT_STATUS, COLLAB_PAY_STATUS, INVOICE_STATUS, eur } from "../../lib/helpers";
import { EmptyState, Skeleton } from "../../components/Feedback";
import KpiSummary from "../../components/KpiSummary";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); };
const today = () => new Date().toISOString().slice(0, 10);
const IN_STATUSES = ["a_venir", "en_attente", "partiellement_paye", "paye", "en_retard", "annule"];
const OUT_STATUSES = ["a_venir", "a_payer", "partiellement_paye", "paye", "en_retard", "annule"];

// Cellule éditable en ligne : montant réellement reçu/payé (saisir, modifier, supprimer).
const PaidAmountCell = ({ e, onSave }) => {
  const [val, setVal] = useState(e.paid_amount > 0 ? String(e.paid_amount) : "");
  useEffect(() => { setVal(e.paid_amount > 0 ? String(e.paid_amount) : ""); }, [e.paid_amount]);
  const num = val === "" ? null : (parseFloat(val) || 0);
  const outstanding = Math.max((e.amount || 0) - (num || 0), 0);
  const commit = () => {
    const cur = e.paid_amount > 0 ? e.paid_amount : null;
    if (num !== cur) onSave(e, val);
  };
  return (
    <div className="shrink-0 w-32 text-right">
      <div className="text-xs uppercase tracking-wider text-marine/80">Réel reçu</div>
      <div className="flex items-center gap-1 justify-end mt-0.5">
        <input type="number" min="0" value={val} disabled={e.locked}
          onChange={(ev) => setVal(ev.target.value)} onBlur={commit}
          onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
          placeholder="Réel" data-testid={`acc-paidamount-${e.id}`}
          className={`w-20 text-xs text-right border border-marine/20 rounded-sm px-1.5 py-1 ${num > 0 ? (e.flow === "in" ? "text-bleu" : "text-bordeaux") : "text-marine/80"} disabled:opacity-50 disabled:cursor-not-allowed`} />
        {val && !e.locked && (
          <button onClick={() => { setVal(""); onSave(e, ""); }} data-testid={`acc-clearamount-${e.id}`}
            title="Supprimer le montant reçu" className="text-bordeaux hover:text-bordeaux-hover transition-colors"><Icons.X size={16} /></button>
        )}
      </div>
      {outstanding > 0 && num > 0 && <div className="text-xs text-ambre-texte">Solde {eur(outstanding)}</div>}
    </div>
  );
};

// Cellule éditable en ligne : date réelle de réception/paiement (modifiable + effaçable).
const PaidDateCell = ({ e, onSave }) => {
  const [val, setVal] = useState(e.paid_date ? e.paid_date.slice(0, 10) : "");
  useEffect(() => { setVal(e.paid_date ? e.paid_date.slice(0, 10) : ""); }, [e.paid_date]);
  return (
    <div className="hidden lg:block shrink-0 w-40">
      <div className="text-xs uppercase tracking-wider text-marine/80 text-right mb-0.5">Reçu le</div>
      <div className="flex items-center gap-1 justify-end">
        <input type="date" value={val} disabled={e.locked}
          onChange={(ev) => { setVal(ev.target.value); onSave(e, ev.target.value); }}
          data-testid={`acc-paiddate-${e.id}`}
          className="text-xs border border-marine/20 rounded-sm px-1.5 py-1 disabled:opacity-50 disabled:cursor-not-allowed" />
        {val && !e.locked && (
          <button onClick={() => { setVal(""); onSave(e, ""); }} data-testid={`acc-cleardate-${e.id}`}
            title="Effacer la date de réception" className="text-bordeaux hover:text-bordeaux-hover transition-colors"><Icons.X size={16} /></button>
        )}
      </div>
    </div>
  );
};

export default function AdminAccounting() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(monthKey(new Date()));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [flow, setFlow] = useState(["in", "out"].includes(searchParams.get("flow")) ? searchParams.get("flow") : "all");
  const [clientId, setClientId] = useState("");
  const [collabId, setCollabId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [status, setStatus] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [curAlert, setCurAlert] = useState(null);
  useEffect(() => { api.get("/admin/currency-settings").then((r) => setCurAlert(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { period, month, year, flow };
    if (clientId) params.client_id = clientId;
    if (collabId) params.collaborator_id = collabId;
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;
    if (invoiceStatus) params.invoice_status = invoiceStatus;
    try { const r = await api.get("/admin/accounting", { params }); setData(r.data); }
    catch { toast.error("Erreur de chargement"); } finally { setLoading(false); }
  }, [period, month, year, flow, clientId, collabId, projectId, status, invoiceStatus]);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => { const [y, mo] = month.split("-").map(Number); setMonth(monthKey(new Date(y, mo - 1 + delta, 1))); };
  const shiftYear = (delta) => setYear(String(Number(year) + delta));
  const resetFilters = () => { setFlow("all"); setClientId(""); setCollabId(""); setProjectId(""); setStatus(""); setInvoiceStatus(""); };

  const sum = data?.summary || {};
  const curSym = data?.currencies || { EUR: "€", USD: "$", AED: "AED" };
  const warnings = data?.warnings || [];
  const money = (n, cur) => (Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " " + (curSym[cur] || cur || "€");
  const enc = sum.encaissements || {}; const dec = sum.decaissements || {}; const res = sum.resultat || {}; const ret = sum.retard || { clients: {}, collaborateurs: {} };
  const opts = data?.options || { clients: [], collaborators: [], projects: [] };
  const entries = data?.entries || [];
  const periodTxt = period === "month" ? monthLabel(month) : period === "year" ? `Année ${year}` : "Toute l'activité";
  const retardTotal = (ret.clients?.amount || 0) + (ret.collaborateurs?.amount || 0);
  const retardCount = (ret.clients?.count || 0) + (ret.collaborateurs?.count || 0);

  const changeStatus = async (e, newStatus) => {
    if (e.locked) { toast.error("Paiement verrouillé — déverrouillez la facturation / rémunération pour le modifier."); return; }
    const body = { status: newStatus };
    if (newStatus === "paye") { body.paid_amount = e.amount; body.paid_date = e.paid_date || today(); }
    try {
      if (e.flow === "in") await api.put(`/admin/projects/${e.source_id}/installments/${e.item_id}`, body);
      else await api.put(`/admin/missions/${e.source_id}/payments/${e.item_id}`, body);
      toast.success("Statut mis à jour"); load();
    } catch (err) { toast.error(err.response?.status === 423 ? "Verrouillé — déverrouillez d'abord." : "Erreur"); }
  };

  const savePaidAmount = async (e, raw) => {
    if (e.locked) { toast.error("Paiement verrouillé — déverrouillez d'abord."); return; }
    const num = raw === "" ? null : (parseFloat(raw) || 0);
    const body = {};
    if (num == null || num <= 0) {
      body.paid_amount = null; body.status = "a_venir"; body.paid_date = "";
    } else if (num >= (e.amount || 0)) {
      body.paid_amount = num; body.status = "paye"; if (!e.paid_date) body.paid_date = today();
    } else {
      body.paid_amount = num; body.status = "partiellement_paye"; if (!e.paid_date) body.paid_date = today();
    }
    try {
      if (e.flow === "in") await api.put(`/admin/projects/${e.source_id}/installments/${e.item_id}`, body);
      else await api.put(`/admin/missions/${e.source_id}/payments/${e.item_id}`, body);
      toast.success(num ? "Montant reçu mis à jour" : "Montant reçu supprimé"); load();
    } catch (err) { toast.error(err.response?.status === 423 ? "Verrouillé — déverrouillez d'abord." : "Erreur"); }
  };

  const savePaidDate = async (e, val) => {
    if (e.locked) { toast.error("Paiement verrouillé — déverrouillez d'abord."); return; }
    try {
      if (e.flow === "in") await api.put(`/admin/projects/${e.source_id}/installments/${e.item_id}`, { paid_date: val });
      else await api.put(`/admin/missions/${e.source_id}/payments/${e.item_id}`, { paid_date: val });
      toast.success(val ? "Date de réception mise à jour" : "Date de réception effacée"); load();
    } catch (err) { toast.error(err.response?.status === 423 ? "Verrouillé — déverrouillez d'abord." : "Erreur"); }
  };

  const exportCsv = async () => {
    const params = { period, month, year, flow };
    if (clientId) params.client_id = clientId;
    if (collabId) params.collaborator_id = collabId;
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;
    if (invoiceStatus) params.invoice_status = invoiceStatus;
    try {
      const r = await api.get("/admin/accounting/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `comptabilite_${period}_${period === "year" ? year : month}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Échec de l'export"); }
  };

  const Row = ({ e }) => {
    const meta = e.flow === "in" ? (PAYMENT_STATUS[e.status] || PAYMENT_STATUS.a_venir) : (COLLAB_PAY_STATUS[e.status] || COLLAB_PAY_STATUS.a_venir);
    const statuses = e.flow === "in" ? IN_STATUSES : OUT_STATUSES;
    const LABELS = e.flow === "in" ? PAYMENT_STATUS : COLLAB_PAY_STATUS;
    const goTo = () => e.flow === "in" ? nav(`/admin/projets/${e.source_id}`) : nav(`/admin/missions/${e.source_id}`);
    return (
      <div className="flex items-center gap-4 px-5 py-4 hover:bg-creme transition-colors" data-testid={`acc-row-${e.id}`}>
        <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${e.flow === "in" ? "bg-bleu-50 text-bleu" : "bg-bordeaux-50 text-bordeaux"}`}
          title={e.flow === "in" ? "Encaissement (entreprise)" : "Décaissement (collaborateur)"}>
          {e.flow === "in" ? <Icons.ArrowDownLeft size={16} /> : <Icons.ArrowUpRight size={16} />}
        </div>
        <button onClick={goTo} className="min-w-0 flex-1 text-left" data-testid={`acc-open-${e.id}`}>
          <div className="text-sm font-medium text-marine truncate">
            {e.label}{e.flow === "in" ? (e.project_name ? ` · ${e.project_name}` : "") : (e.mission_name ? ` · ${e.mission_name}` : "")}
          </div>
          <div className="text-xs text-marine/80 truncate">
            {e.flow === "in"
              ? `${e.client_name || "—"}${e.type_label ? ` · ${e.type_label}` : ""}${e.reference ? ` · ${e.reference}` : ""}`
              : `${e.collaborator_name || "—"}${e.project_name ? ` · ${e.project_name}` : ""}${e.reference ? ` · ${e.reference}` : ""}`}
          </div>
          {e.flow === "in" && e.currency && e.currency !== "EUR" && (
            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-sm border mt-1 mr-1 border-marine/25 text-marine bg-marine-50" data-testid={`acc-currency-${e.id}`}>{(curSym[e.currency] && curSym[e.currency] !== e.currency) ? `${curSym[e.currency]} ` : ""}{e.currency}</span>
          )}
          {e.flow === "in" && e.invoice_status && INVOICE_STATUS[e.invoice_status] && (
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-sm border mt-1 ${INVOICE_STATUS[e.invoice_status].cls}`} data-testid={`acc-invoice-status-${e.id}`}>Facture : {INVOICE_STATUS[e.invoice_status].label}</span>
          )}
        </button>
        <div className="hidden lg:block text-right shrink-0 w-24">
          <div className="text-xs uppercase tracking-wider text-marine/80">Prévue</div>
          <div className="text-xs text-marine">{e.planned_date ? fmtDate(e.planned_date) : "—"}</div>
        </div>
        <PaidDateCell e={e} onSave={savePaidDate} />
        <div className="hidden md:block text-right shrink-0 w-28">
          <div className="text-xs uppercase tracking-wider text-marine/80">Prévu</div>
          <div className="text-sm text-marine mt-0.5">{money(e.amount, e.currency)}</div>
          {e.currency && e.currency !== "EUR" && (
            <div className="text-xs text-marine/80" data-testid={`acc-eur-${e.id}`}>
              {e.converted_planned ? `≈ ${eur(e.amount_eur)}` : <span className="text-rouge">taux manquant</span>}
            </div>
          )}
        </div>
        <PaidAmountCell e={e} onSave={savePaidAmount} />
        <div className="shrink-0 w-44 flex items-center justify-end gap-1.5">
          {e.locked && <Icons.Lock size={16} className="text-marine" title="Verrouillé" />}
          <select value={e.status} disabled={e.locked} onChange={(ev) => changeStatus(e, ev.target.value)}
            data-testid={`acc-status-${e.id}`}
            className={`text-[11px] px-2 py-1.5 rounded-sm border font-medium w-full ${meta.cls} disabled:opacity-60 disabled:cursor-not-allowed`}>
            {statuses.map((k) => <option key={k} value={k}>{LABELS[k].label}</option>)}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <PageTitle overline="Comptabilité" title="Cockpit financier" />
        <button onClick={exportCsv} data-testid="export-csv-btn" className="mt-1 inline-flex items-center gap-2 bg-marine text-white rounded-sm px-4 py-2.5 text-sm hover:bg-marine-hover transition-colors">
          <Icons.Download size={16} /> Exporter CSV
        </button>
      </div>

      {/* Période */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex rounded-sm border border-marine/20 overflow-hidden" data-testid="period-toggle">
          {[["month", "Mois"], ["year", "Année"], ["all", "Tout"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
              className={`px-4 py-2 text-sm transition-colors ${period === k ? "bg-marine text-white" : "text-marine hover:bg-marine-50"}`}>{lbl}</button>
          ))}
        </div>
        {period === "month" && (
          <div className="flex flex-wrap items-center gap-2">
            <button aria-label="Mois précédent" onClick={() => shiftMonth(-1)} data-testid="month-prev" className="w-9 h-9 rounded-sm border border-marine/20 flex items-center justify-center hover:bg-marine hover:text-white transition-colors"><Icons.ChevronLeft size={16} /></button>
            <div className="font-display text-lg text-marine capitalize min-w-[110px] sm:min-w-[160px] text-center" data-testid="current-period">{monthLabel(month)}</div>
            <button aria-label="Mois suivant" onClick={() => shiftMonth(1)} data-testid="month-next" className="w-9 h-9 rounded-sm border border-marine/20 flex items-center justify-center hover:bg-marine hover:text-white transition-colors"><Icons.ChevronRight size={16} /></button>
            <input aria-label="Mois affiché" type="month" value={month} onChange={(ev) => ev.target.value && setMonth(ev.target.value)} data-testid="month-picker" className="w-full sm:w-auto text-sm border border-marine/20 rounded-sm p-1.5" />
          </div>
        )}
        {period === "year" && (
          <div className="flex items-center gap-2">
            <button onClick={() => shiftYear(-1)} data-testid="year-prev" className="w-9 h-9 rounded-sm border border-marine/20 flex items-center justify-center hover:bg-marine hover:text-white transition-colors"><Icons.ChevronLeft size={16} /></button>
            <div className="font-display text-lg text-marine min-w-[100px] text-center" data-testid="current-period">{year}</div>
            <button onClick={() => shiftYear(1)} data-testid="year-next" className="w-9 h-9 rounded-sm border border-marine/20 flex items-center justify-center hover:bg-marine hover:text-white transition-colors"><Icons.ChevronRight size={16} /></button>
          </div>
        )}
        {period === "all" && <div className="font-display text-lg text-marine" data-testid="current-period">Toute l'activité</div>}
      </div>

      {/* Rappel : taux du mois courant manquants (devises actives) */}
      {curAlert?.reminder_enabled && (curAlert.missing_this_month || []).length > 0 && (
        <div className="bg-ambre-50 border border-ambre-vif/40 rounded-sm p-4 mb-5 flex items-center gap-3" data-testid="currency-month-reminder">
          <Icons.CalendarClock size={16} className="text-ambre shrink-0" />
          <span className="text-sm text-ambre-texte flex-1">Taux de change à renseigner pour ce mois : <strong>{(curAlert.missing_this_month).join(", ")}</strong>.</span>
          <button onClick={() => nav("/admin/comptabilite/devises")} className="shrink-0 inline-flex items-center gap-1 text-ambre-texte font-medium hover:underline" data-testid="reminder-goto-currencies">Renseigner <Icons.ArrowRight size={16} /></button>
        </div>
      )}

      {/* Avertissements : taux de change manquants */}
      {warnings.length > 0 && (
        <div className="bg-rouge-50 border border-rouge/30 rounded-sm p-4 mb-5 flex gap-3" data-testid="rate-warnings">
          <Icons.AlertTriangle size={16} className="text-rouge shrink-0 mt-0.5" />
          <div className="text-sm text-rouge-texte">
            <p className="font-medium mb-1">Taux de change manquants — certains montants ne sont pas convertis en euros :</p>
            <ul className="list-disc list-inside space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i} data-testid={`warning-${w.currency}-${w.month}`}>{w.currency} · {monthLabel(w.month)} ({w.count} ligne(s))</li>
              ))}
            </ul>
            <button onClick={() => nav("/admin/comptabilite/devises")} className="mt-2 inline-flex items-center gap-1 text-rouge font-medium hover:underline" data-testid="goto-currencies">
              Définir les taux <Icons.ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Cartes récapitulatives */}
      <KpiSummary className="mb-6" items={[
        { testid: "card-encaissements", accent: "text-bleu", label: "Encaissements", value: eur(enc.realise), sub: `Prévu : ${eur(enc.previsionnel)}` },
        { testid: "card-decaissements", accent: "text-bordeaux", label: "Décaissements", value: eur(dec.realise), sub: `Prévu : ${eur(dec.previsionnel)}` },
        { testid: "card-resultat", accent: (res.realise || 0) >= 0 ? "text-bleu" : "text-rouge", label: "Résultat (solde)", value: `${(res.realise || 0) >= 0 ? "+" : ""}${eur(res.realise)}`, sub: `Prévisionnel : ${(res.previsionnel || 0) >= 0 ? "+" : ""}${eur(res.previsionnel)}` },
        { testid: "card-a-encaisser", label: "À encaisser", value: eur(enc.attente), sub: "Échéances entreprises non réglées" },
        { testid: "card-a-payer", label: "À payer", value: eur(dec.avenir), sub: "Rémunérations collab. à venir" },
        { testid: "card-retard", accent: "text-rouge", label: "En retard", value: eur(retardTotal), sub: `${retardCount} paiement(s) · entreprises ${eur(ret.clients?.amount)} / collab ${eur(ret.collaborateurs?.amount)}` },
      ]} />

      {/* Filtres */}
      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="accounting-filters">
        <div className="flex rounded-sm border border-marine/20 overflow-hidden" data-testid="flow-toggle">
          {[["all", "Tout"], ["in", "Encaissements"], ["out", "Décaissements"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setFlow(k)} data-testid={`flow-${k}`}
              className={`px-1.5 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm transition-colors ${flow === k ? "bg-marine text-white" : "text-marine hover:bg-marine-50"}`}>{lbl}</button>
          ))}
        </div>
        <label className="text-xs text-marine/80">Entreprise
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="filter-client" className="block mt-1 text-sm border border-marine/20 rounded-sm p-2 min-w-[160px]">
            <option value="">Tous</option>
            {opts.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-marine/80">Collaborateur
          <select value={collabId} onChange={(e) => setCollabId(e.target.value)} data-testid="filter-collab" className="block mt-1 text-sm border border-marine/20 rounded-sm p-2 min-w-[160px]">
            <option value="">Tous</option>
            {opts.collaborators.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-marine/80">Projet
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="filter-project" className="block mt-1 text-sm border border-marine/20 rounded-sm p-2 min-w-[160px]">
            <option value="">Tous</option>
            {opts.projects.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-marine/80">Statut
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="block mt-1 text-sm border border-marine/20 rounded-sm p-2 min-w-[140px]">
            <option value="">Tous</option>
            <option value="a_venir">À venir</option>
            <option value="en_attente">En attente</option>
            <option value="a_payer">À payer</option>
            <option value="partiellement_paye">Partiellement payé</option>
            <option value="paye">Payé</option>
            <option value="en_retard">En retard</option>
            <option value="annule">Annulé</option>
          </select>
        </label>
        <label className="text-xs text-marine/80">Statut facture
          <select value={invoiceStatus} onChange={(e) => setInvoiceStatus(e.target.value)} data-testid="filter-invoice-status" className="block mt-1 text-sm border border-marine/20 rounded-sm p-2 min-w-[140px]">
            <option value="">Toutes</option>
            <option value="emise">Émise</option>
            <option value="en_attente">En attente</option>
            <option value="paye">Payée</option>
          </select>
        </label>
        <button onClick={resetFilters} data-testid="reset-filters" className="text-sm text-bordeaux hover:underline inline-flex items-center gap-1 pb-2"><Icons.X size={16} /> Réinitialiser</button>
      </div>

      {/* Liste des flux */}
      <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-marine/10 bg-creme">
          <span className="text-sm font-medium text-marine">Flux financiers · <span className="capitalize">{periodTxt}</span></span>
          <span className="text-sm text-marine/80" data-testid="entries-count">{entries.length} ligne(s)</span>
        </div>
        {loading && (
          <div className="p-5 space-y-3" data-testid="accounting-loading">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="w-9 h-9 !rounded-full shrink-0" />
                <div className="flex-1"><Skeleton className="h-4 w-1/3 mb-2" /><Skeleton className="h-3 w-1/4" /></div>
                <Skeleton className="h-8 w-24 shrink-0" />
              </div>
            ))}
          </div>
        )}
        {!loading && entries.length === 0 && (
          <div data-testid="no-entries"><EmptyState icon="Receipt" title="Aucun flux sur cette période" hint="Renseignez les échéances (projets) et les rémunérations (missions) pour voir apparaître les flux financiers ici." /></div>
        )}
        <div className="divide-y divide-marine/5">
          {entries.map((e) => <Row key={`${e.flow}-${e.id}`} e={e} />)}
        </div>
      </div>
    </div>
  );
}
